import { useCallback, useEffect, useState } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { RollupExplanationResponse } from '../../../types/statusFactors';

/** How often a `pending` explanation is read again, and how many times, before the hook reports it stalled (~15s in all). */
export const PENDING_POLL_INTERVAL_MS = 3000;
export const MAX_PENDING_POLLS = 5;

export interface UseRollupExplanationResult {
  data: RollupExplanationResponse | null;
  loading: boolean;
  error: Error | null;
  /** The explanation is still `pending` after every scheduled re-read: nothing recalculated the factor in that window. */
  stalled: boolean;
  reload: () => void;
}

/**
 * Fetches one rollup factor's redacted "why is it this colour?" explanation. A different factor drops the previous one's data.
 * While the server answers `pending`, the hook reads again every {@link PENDING_POLL_INTERVAL_MS} up to {@link MAX_PENDING_POLLS}
 * times, then reports `stalled`; `reload` starts a fresh round.
 */
export function useRollupExplanation(sphereId: string | null, factorId: string | null): UseRollupExplanationResult {
  const graphClient = useApiClientsOrNull()?.graphClient;
  const [data, setData] = useState<RollupExplanationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [pendingPolls, setPendingPolls] = useState(0);

  const scopeKey = `${sphereId ?? ''}/${factorId ?? ''}`;
  const [renderedScopeKey, setRenderedScopeKey] = useState(scopeKey);
  if (renderedScopeKey !== scopeKey) {
    setRenderedScopeKey(scopeKey);
    setData(null);
    setError(null);
    setPendingPolls(0);
  }

  useEffect(() => {
    if (!graphClient || !sphereId || !factorId) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    graphClient
      .get<RollupExplanationResponse>(`/spheres/${sphereId}/factors/${factorId}/explanation`, { signal: controller.signal })
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [graphClient, sphereId, factorId, reloadToken]);

  const pending = Boolean(data?.pending);
  useEffect(() => {
    if (!pending || loading || error || pendingPolls >= MAX_PENDING_POLLS) return undefined;
    const timer = setTimeout(() => {
      setPendingPolls((n) => n + 1);
      setReloadToken((n) => n + 1);
    }, PENDING_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pending, loading, error, pendingPolls]);

  const reload = useCallback(() => {
    setPendingPolls(0);
    setReloadToken((n) => n + 1);
  }, []);

  const stalled = pending && !loading && !error && pendingPolls >= MAX_PENDING_POLLS;
  return { data, loading, error, stalled, reload };
}
